import { useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { AlertCircle, LoaderCircle } from 'lucide-react'
import PageIntro from '../components/ui/PageIntro'
import Section from '../components/ui/Section'
import Button from '../components/ui/Button'
import { AccountField, AccountPanel, FIELD_CLASS } from '../components/account/AccountUi'
import { useAccount } from '../hooks/account-context'
import { toApiError } from '../lib/http'

/* Making an account.

   The mobile number is the important field, and not because we need to ring
   anybody: it is how the showroom already files people. A visitor who booked
   a car last spring is on file under that number, and registering with it
   attaches this sign-in to that record rather than starting a second one —
   which is the difference between an account page that opens on their
   booking and one that opens empty for the very people who have the most to
   look at.

   The role is not on this form and never could be: the API writes Customer
   itself, so a crafted request cannot ask for anything else. */

const PHONE_PATTERN = /^(\+92|0)?[\s-]?3\d{2}[\s-]?\d{7}$/

export function Register() {
  const { status, register } = useAccount()

  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fields, setFields] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  if (status === 'signed-in') return <Navigate to="/account" replace />

  /* Checked here as a courtesy to whoever is typing — the API's rules are
     the ones that decide, and anything it refuses comes back per field. */
  function check() {
    const found: Record<string, string> = {}
    if (fullName.trim().length < 2) found.fullName = 'Please tell us your name.'
    if (!/^[a-zA-Z0-9._-]{3,40}$/.test(username.trim())) {
      found.username = 'Three characters or more — letters, numbers, a dot, a dash or an underscore.'
    }
    if (!PHONE_PATTERN.test(phone.trim())) found.phone = 'Enter a valid mobile number.'
    if (password.length < 8) found.password = 'Use at least eight characters.'
    return found
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy) return

    const found = check()
    setFields(found)
    setError('')
    if (Object.keys(found).length > 0) return

    setBusy(true)

    try {
      await register({ fullName, username, phone, email, password })
      /* Registering signs you in, so the redirect above takes it from here. */
    } catch (failure) {
      const problem = toApiError(failure)
      setFields(problem.fields ?? {})
      if (!problem.fields) setError(problem.message)
      setBusy(false)
    }
  }

  return (
    <>
      <PageIntro
        eyebrow="Your account"
        title="Create an account"
        ghost="Account"
        lede="One sign-in for everything you have with us — your orders, what you have paid, and the months left on a bank plan."
      />

      <Section className="!py-16 sm:!py-20">
        <div className="mx-auto w-full max-w-[520px]">
          <AccountPanel>
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
              {error ? (
                <p
                  role="alert"
                  className="flex items-start gap-2.5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-[13px] leading-relaxed text-red-200"
                >
                  <AlertCircle aria-hidden="true" className="mt-px size-4 shrink-0" strokeWidth={2} />
                  {error}
                </p>
              ) : null}

              <AccountField label="Full name" htmlFor="register-name" error={fields.fullName}>
                <input
                  id="register-name"
                  value={fullName}
                  autoFocus
                  autoComplete="name"
                  onChange={(event) => setFullName(event.target.value)}
                  className={FIELD_CLASS}
                />
              </AccountField>

              <AccountField
                label="Mobile number"
                htmlFor="register-phone"
                error={fields.phone}
                hint="If you have booked with us before, use the same number and your file comes with you."
              >
                <input
                  id="register-phone"
                  type="tel"
                  value={phone}
                  autoComplete="tel"
                  placeholder="0300 1234567"
                  onChange={(event) => setPhone(event.target.value)}
                  className={FIELD_CLASS}
                />
              </AccountField>

              <AccountField label="Username" htmlFor="register-username" error={fields.username}>
                <input
                  id="register-username"
                  value={username}
                  autoComplete="username"
                  onChange={(event) => setUsername(event.target.value)}
                  className={FIELD_CLASS}
                />
              </AccountField>

              <AccountField label="Email" htmlFor="register-email" optional error={fields.email}>
                <input
                  id="register-email"
                  type="email"
                  value={email}
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                  className={FIELD_CLASS}
                />
              </AccountField>

              <AccountField
                label="Password"
                htmlFor="register-password"
                error={fields.password}
                hint="Eight characters or more."
              >
                <input
                  id="register-password"
                  type="password"
                  value={password}
                  autoComplete="new-password"
                  onChange={(event) => setPassword(event.target.value)}
                  className={FIELD_CLASS}
                />
              </AccountField>

              <Button type="submit" size="lg" disabled={busy} className="w-full">
                {busy ? (
                  <>
                    <LoaderCircle aria-hidden="true" className="size-4 animate-spin" strokeWidth={2} />
                    Creating
                  </>
                ) : (
                  'Create account'
                )}
              </Button>
            </form>
          </AccountPanel>

          <p className="mt-6 text-center text-[13.5px] text-steel">
            Already have one?{' '}
            <Link to="/sign-in" className="font-medium text-lime hover:text-lime-lift">
              Sign in
            </Link>
          </p>
        </div>
      </Section>
    </>
  )
}

export default Register
